/**
 * Bishop Score (Bishop EH. 1964; modifikasi Calder) — kalkulator FE.
 * Harus konsisten dengan App\Support\BishopScoreCalculator (backend authoritative).
 * FE hanya untuk preview live; nilai final tetap dihitung ulang di server.
 */

export type Konsistensi = "firm" | "medium" | "soft";
export type PosisiServiks = "posterior" | "mid" | "anterior";
export type BishopInterpretasi = "favorable" | "equivocal" | "unfavorable";
export type BishopModifier =
  | "preeklampsia"
  | "postdate"
  | "nulipara"
  | "multipara"
  | "kpd"
  | "riwayat_pervaginam_aterm";

export const MODIFIER_ADJUSTMENT: Record<BishopModifier, number> = {
  preeklampsia: -1,
  postdate: -1,
  nulipara: -1,
  multipara: 1,
  kpd: 1,
  riwayat_pervaginam_aterm: 1
};

export interface BishopVtInput {
  dilatasi_cm?: number | null;
  effacement_persen?: number | null;
  station?: number | null;
  konsistensi?: Konsistensi | null;
  posisi?: PosisiServiks | null;
}

export interface BishopComponents {
  dilatasi: number | null;
  effacement: number | null;
  station: number | null;
  konsistensi: number | null;
  posisi: number | null;
}

export interface BishopResult {
  komponen: BishopComponents;
  total: number | null;
  modifier_delta: number;
  modified_total: number | null;
  interpretasi: BishopInterpretasi | null;
}

export function skorDilatasi(cm: number | null | undefined): number | null {
  if (cm == null) return null;
  if (cm <= 0) return 0;
  if (cm < 3) return 1; // 1-2
  if (cm < 5) return 2; // 3-4
  return 3; // >=5
}

export function skorEffacement(p: number | null | undefined): number | null {
  if (p == null) return null;
  if (p < 40) return 0; // 0-30
  if (p < 60) return 1; // 40-50
  if (p < 80) return 2; // 60-70
  return 3; // >=80
}

export function skorStation(s: number | null | undefined): number | null {
  if (s == null) return null;
  if (s <= -3) return 0;
  if (s === -2) return 1;
  if (s === -1 || s === 0) return 2;
  return 3; // >= +1
}

export function skorKonsistensi(k: Konsistensi | null | undefined): number | null {
  if (k === "firm") return 0;
  if (k === "medium") return 1;
  if (k === "soft") return 2;
  return null;
}

export function skorPosisi(p: PosisiServiks | null | undefined): number | null {
  if (p === "posterior") return 0;
  if (p === "mid") return 1;
  if (p === "anterior") return 2;
  return null;
}

export function interpretasi(
  total: number | null
): BishopInterpretasi | null {
  if (total == null) return null;
  if (total >= 8) return "favorable";
  if (total >= 5) return "equivocal";
  return "unfavorable";
}

export function interpretasiLabel(i: BishopInterpretasi | null): string {
  switch (i) {
    case "favorable":
      return "Serviks Matang (Favorable) — induksi langsung";
    case "equivocal":
      return "Intermediate (Equivocal) — pertimbangkan pematangan/induksi";
    case "unfavorable":
      return "Serviks Belum Matang (Unfavorable) — wajib pematangan serviks dulu";
    default:
      return "";
  }
}

export function computeBishop(
  vt: BishopVtInput,
  mods: BishopModifier[] = []
): BishopResult {
  const komponen: BishopComponents = {
    dilatasi: skorDilatasi(vt.dilatasi_cm),
    effacement: skorEffacement(vt.effacement_persen),
    station: skorStation(vt.station),
    konsistensi: skorKonsistensi(vt.konsistensi ?? null),
    posisi: skorPosisi(vt.posisi ?? null)
  };

  const filled = Object.values(komponen).filter(
    (v): v is number => v !== null
  );
  const total = filled.length > 0 ? filled.reduce((a, b) => a + b, 0) : null;

  let delta = 0;
  mods.forEach(m => {
    delta += MODIFIER_ADJUSTMENT[m] ?? 0;
  });
  const modified_total = total == null ? null : total + delta;

  return {
    komponen,
    total,
    modifier_delta: delta,
    modified_total,
    interpretasi: interpretasi(total)
  };
}
